import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService, TenantSettingsResponse } from '../services/api';
import { getResolvedTenantId, getStoredAuthRole, LS_TENANT_ID, persistApiCredentials, TENANT_CREDENTIALS_EVENT, getResolvedApiToken } from '../config/api-config';
import {
  currencyCodeFromProfile,
  formatMoneyCents,
  minorUnitLabel,
  symbolForCurrency,
} from '../utils/money';

export const LS_CURRENCY_CODE = 'rust-school-currency-code';

type TenantCurrencyContextValue = {
  currencyCode: string;
  currencySymbol: string;
  minorUnitLabel: string;
  formatMoney: (cents: number) => string;
  tenant: TenantSettingsResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

function normalizeTenant(row: TenantSettingsResponse): TenantSettingsResponse {
  const code = currencyCodeFromProfile(row.currency_code, row.country_code);
  return {
    ...row,
    currency_code: code,
    currency_symbol: row.currency_symbol?.trim() || symbolForCurrency(code),
  };
}

function profileToTenant(profile: Record<string, unknown>): TenantSettingsResponse | null {
  const id = profile.id;
  if (id == null || String(id).trim() === '') return null;
  const currencyCode = currencyCodeFromProfile(
    profile.currency_code as string | undefined,
    profile.country_code as string | undefined
  );
  const boardCode = String(profile.board_code ?? 'cbse').toLowerCase();
  const tenantId = typeof id === 'number' ? id : Number.parseInt(String(id), 10);
  const logoUrl =
    typeof profile.logo_url === 'string' && profile.logo_url.trim()
      ? profile.logo_url.trim()
      : typeof profile.logoUrl === 'string' && profile.logoUrl.trim()
        ? profile.logoUrl.trim()
        : typeof profile.school_logo_url === 'string' && profile.school_logo_url.trim()
          ? profile.school_logo_url.trim()
          : null;
  const logoMediaId =
    typeof profile.logo_media_id === 'string' && profile.logo_media_id.trim()
      ? profile.logo_media_id.trim()
      : typeof profile.logoMediaId === 'string' && profile.logoMediaId.trim()
        ? profile.logoMediaId.trim()
        : null;

  const residenceMode =
    typeof profile.student_residence_mode === 'string'
      ? profile.student_residence_mode
      : typeof profile.studentResidenceMode === 'string'
        ? profile.studentResidenceMode
        : null;

  return normalizeTenant({
    ...profile,
    tenant_id: Number.isFinite(tenantId) ? tenantId : 0,
    tenant_sequence:
      typeof profile.tenant_sequence === 'number' ? profile.tenant_sequence : null,
    name: String(profile.name ?? ''),
    org_code: (profile.org_code as string | null | undefined) ?? null,
    org_type: String(profile.org_type ?? 'school'),
    board_code: boardCode === 'icse' ? 'icse' : 'cbse',
    country_code: (profile.country_code as string | null | undefined) ?? null,
    currency_code: currencyCode,
    currency_symbol: symbolForCurrency(currencyCode),
    logo_url: logoUrl,
    logo_media_id: logoMediaId,
    student_residence_mode: residenceMode,
  });
}

async function fetchTenantFromApi(): Promise<TenantSettingsResponse | null> {
  // Same tenant row as DB: /api/school exposes currency_code; /tenants/me adds symbol.
  try {
    const school = await apiService.getSchoolProfile();
    if (school.data) {
      const mapped = profileToTenant(school.data as Record<string, unknown>);
      if (mapped) return mapped;
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[currency] GET /api/school failed:', e);
    }
  }

  try {
    const me = await apiService.getTenantSettings();
    if (me.data?.currency_code) return normalizeTenant(me.data);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[currency] GET /tenants/me failed:', e);
    }
  }

  return null;
}

function readCachedCurrency(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const c = localStorage.getItem(LS_CURRENCY_CODE);
  return c && c.trim() ? c.trim().toUpperCase() : null;
}

const defaultValue: TenantCurrencyContextValue = {
  currencyCode: 'USD',
  currencySymbol: '$',
  minorUnitLabel: 'cents',
  formatMoney: (cents) => formatMoneyCents(cents, '$', 'USD'),
  tenant: null,
  loading: true,
  error: null,
  reload: () => {},
};

const TenantCurrencyContext = createContext<TenantCurrencyContextValue>(defaultValue);

export function TenantCurrencyProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<TenantSettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    const onCredentialsChanged = () => reload();
    window.addEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
    return () => window.removeEventListener(TENANT_CREDENTIALS_EVENT, onCredentialsChanged);
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getStoredAuthRole() === 'super_admin') {
        setTenant(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Avoid authenticated probes while login is clearing/swapping credentials.
      if (!getResolvedApiToken().trim()) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      const tenantId = getResolvedTenantId();

      try {
        const loaded = await fetchTenantFromApi();
        if (cancelled) return;
        if (loaded) {
          setTenant(loaded);
          localStorage.setItem(LS_CURRENCY_CODE, loaded.currency_code);
          if (!getResolvedTenantId().trim() && loaded.tenant_id > 0) {
            persistApiCredentials(undefined, String(loaded.tenant_id));
          }
          setError(null);
          return;
        }

        const cached = readCachedCurrency();
        if (cached) {
          const parsedTenantId = Number.parseInt(tenantId, 10);
          setTenant({
            tenant_id: Number.isFinite(parsedTenantId) ? parsedTenantId : 0,
            name: '',
            org_type: 'school',
            board_code: 'cbse',
            country_code: null,
            currency_code: cached,
            currency_symbol: symbolForCurrency(cached),
          });
          setError('Using cached currency; could not reach school API. Check token and backend.');
          return;
        }

        setTenant(null);
        setError(
          tenantId
            ? 'Could not load currency from API. Check Bearer token, backend, and that this tenant id exists in the database.'
            : `Could not load currency. Sign in again, or set REACT_APP_TENANT_ID in .env.development.local for local dev.`
        );
      } catch (e) {
        if (!cancelled) {
          setTenant(null);
          setError(e instanceof Error ? e.message : 'Could not load school currency');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const currencyCode =
    tenant?.currency_code ??
    readCachedCurrency() ??
    currencyCodeFromProfile(null, tenant?.country_code);
  const currencySymbol = tenant?.currency_symbol ?? symbolForCurrency(currencyCode);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyCents(cents, currencySymbol, currencyCode),
    [currencySymbol, currencyCode]
  );

  const value = useMemo(
    (): TenantCurrencyContextValue => ({
      currencyCode,
      currencySymbol,
      minorUnitLabel: minorUnitLabel(currencyCode),
      formatMoney,
      tenant,
      loading,
      error,
      reload,
    }),
    [currencyCode, currencySymbol, formatMoney, tenant, loading, error, reload]
  );

  return <TenantCurrencyContext.Provider value={value}>{children}</TenantCurrencyContext.Provider>;
}

/** Tenant `currency_code` from the database (via school/tenant API). */
export function useTenantCurrency(): TenantCurrencyContextValue {
  return useContext(TenantCurrencyContext);
}
